import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, ArrowRight, Share2, Clock, FileCheck2, Wallet } from "lucide-react";
import { API, rupiah, pesanGalat, BACKEND } from "../lib/api";
import { KerangkaBaris } from "../components/kit";

export default function BuktiDampak() {
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [galat, setGalat] = useState(null);
  const [salin, setSalin] = useState(false);
  const [gagalSalin, setGagalSalin] = useState(null);
  const kunci = params.get("perusahaan");

  useEffect(() => {
    setD(null); setGalat(null);
    API.get(`/bukti-dampak${kunci ? `?perusahaan=${encodeURIComponent(kunci)}` : ""}`)
      .then((r) => setD(r.data))
      .catch((e) => setGalat(pesanGalat(e)));
  }, [kunci]);

  const bagikan = async () => {
    const tautan = d?.perusahaan?.kode ? `${BACKEND}/api/bagikan/${d.perusahaan.kode}` : window.location.href;
    try {
      if (navigator.share) { await navigator.share({ title: "Bukti dampak PAM-IT", url: tautan }); return; }
      if (!navigator.clipboard?.writeText) throw new Error("tanpa clipboard");
      await navigator.clipboard.writeText(tautan);
      setSalin(true);
      setTimeout(() => setSalin(false), 2500);
    } catch {
      setGagalSalin(tautan);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="kaca border-b sticky top-0 z-20">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <Link to="/" data-testid="tautan-beranda" className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-primary" aria-hidden="true" />
            <span className="font-heading font-bold text-lg">PAM-IT</span>
          </Link>
          <div className="flex items-center gap-2">
            <button data-testid="btn-bagikan" onClick={bagikan}
              className="tombol-garis px-3 py-1.5 rounded text-sm inline-flex items-center gap-2">
              <Share2 size={14} /> {salin ? "Tautan tersalin" : "Bagikan"}
            </button>
            <Link to="/daftar" data-testid="tautan-daftar" className="tombol-utama px-3 py-1.5 rounded text-sm">
              <span className="hidden sm:inline">Coba untuk perusahaan saya</span>
              <span className="sm:hidden">Coba</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 md:px-8 py-14 max-w-[1100px] w-full mx-auto">
        {gagalSalin && (
          <p className="mb-8 rounded border border-accent/50 bg-accent/10 p-3 text-sm" data-testid="pesan-gagal-salin">
            Browser Anda menolak menyalin otomatis. Ini tautannya, sorot lalu tekan Ctrl+C:{" "}
            <span className="font-data break-all">{gagalSalin}</span>
          </p>
        )}
        {galat && (
          <div className="panel rounded p-8 max-w-lg" data-testid="galat-bukti">
            <p className="label-kecil">Tidak bisa ditampilkan</p>
            <h1 className="mt-3 text-2xl font-bold">{galat}</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Periksa lagi kode perusahaannya, atau lihat halaman ini memakai data contoh.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/bukti-dampak" data-testid="btn-pakai-contoh" className="tombol-utama px-4 py-2 rounded text-sm">
                Lihat pakai data contoh
              </Link>
              <Link to="/" className="tombol-garis px-4 py-2 rounded text-sm">Ke beranda</Link>
            </div>
          </div>
        )}
        {!galat && d === null && <KerangkaBaris testid="memuat-bukti" n={5} />}
        {d && (
          <>
            <p className="label-kecil">Bukti dampak {d.perusahaan.contoh ? "· data contoh" : `· ${d.perusahaan.nama}`}</p>
            {d.pemeriksaan_auditor && (
              <p data-testid="lencana-auditor"
                className="mt-4 inline-flex items-center gap-2 rounded border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary">
                <ShieldCheck size={14} aria-hidden="true" />
                Sudah dibuka auditor · periode {d.pemeriksaan_auditor.bulan}
                {d.pemeriksaan_auditor.terakhir_dibuka
                  ? ` · ${new Date(d.pemeriksaan_auditor.terakhir_dibuka).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}`
                  : ""}
              </p>
            )}
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold max-w-3xl leading-[1.08]" data-testid="judul-bukti">
              Sebelum dan sesudah akses dicatat dengan rapi
            </h1>
            <p className="mt-5 text-muted-foreground max-w-2xl leading-relaxed">
              Semua angka di bawah dihitung langsung dari catatan yang ada, bukan perkiraan. Angka "sebelum"
              berasal dari pengukuran manual yang sumbernya ikut ditulis, supaya bisa dibantah kalau salah.
            </p>

            <div className="mt-12 space-y-4">
              {d.baris.map((b) => {
                const bandingBisa = b.sebelum != null && b.sesudah != null;
                const naik = bandingBisa && Number(b.sesudah) > Number(b.sebelum);
                const lebihBanyakBagus = b.kunci === "non_akses_tercatat";
                const membaik = bandingBisa && (lebihBanyakBagus ? naik : !naik);
                return (
                  <div key={b.kunci} data-testid={`baris-${b.kunci}`} className="panel panel-hover rounded p-6">
                    <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
                      <div className="min-w-[190px]">
                        <p className="label-kecil">{b.label}</p>
                        <p className="mt-2 text-xs text-muted-foreground">satuan: {b.satuan}</p>
                      </div>
                      <div>
                        <p className="label-kecil">Sebelum</p>
                        <p className="font-data text-3xl font-bold mt-1 text-muted-foreground">{b.sebelum ?? "—"}</p>
                      </div>
                      <ArrowRight className={membaik ? "text-primary mb-3" : "text-accent mb-3"} size={22} aria-hidden="true" />
                      <div>
                        <p className="label-kecil">Sesudah</p>
                        <p className={`font-data text-3xl font-bold mt-1 ${membaik ? "text-primary" : "text-accent"}`}>
                          {b.sesudah ?? "—"}
                        </p>
                      </div>
                      {bandingBisa && (
                        <span data-testid={`arah-${b.kunci}`}
                          className={`mb-2 text-xs font-medium px-2 py-0.5 rounded border ${membaik ? "text-primary border-primary/40 bg-primary/10" : "text-accent border-accent/40 bg-accent/10"}`}>
                          {membaik ? "membaik" : "belum membaik — angkanya masih naik"}
                        </span>
                      )}
                    </div>
                    <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
                      {b.cara_hitung}
                      {b.sumber_sebelum ? ` Sumber angka sebelum: ${b.sumber_sebelum}.` : ""}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="garis-cahaya my-12" />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="panel rounded p-5" data-testid="kartu-penutupan">
                <Clock size={18} className="text-primary" aria-hidden="true" />
                <p className="label-kecil mt-3">Penutupan tercatat</p>
                <p className="font-data text-3xl font-bold mt-2">{d.penutupan_tercatat}</p>
              </div>
              <div className="panel rounded p-5" data-testid="kartu-jam-hemat">
                <Clock size={18} className="text-accent" aria-hidden="true" />
                <p className="label-kecil mt-3">Kerja manual tergantikan</p>
                <p className="font-data text-3xl font-bold mt-2">
                  {d.jam_manual_tergantikan != null ? `${d.jam_manual_tergantikan} jam` : "—"}
                </p>
              </div>
              <div className="panel rounded p-5" data-testid="kartu-surat">
                <FileCheck2 size={18} className="text-primary" aria-hidden="true" />
                <p className="label-kecil mt-3">Surat bebas akses terbit</p>
                <p className="font-data text-3xl font-bold mt-2">{d.surat_terbit}</p>
              </div>
              <div className="panel rounded p-5" data-testid="kartu-uang">
                <Wallet size={18} className="text-destructive" aria-hidden="true" />
                <p className="label-kecil mt-3">Uang yang masih bocor</p>
                <p className="font-data text-2xl font-bold mt-2">
                  {d.uang_masih_bocor ? rupiah(d.uang_masih_bocor) : "belum bisa dihitung"}
                </p>
              </div>
            </div>
            <p className="mt-5 text-xs text-muted-foreground max-w-3xl leading-relaxed" data-testid="catatan-uang">
              {d.catatan_uang}
            </p>

            <div className="mt-16 panel rounded p-8 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold">Mau angka seperti ini untuk perusahaan Anda?</h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Buat ruang kerja sendiri, catat akses seperti biasa, lalu halaman ini terisi otomatis.
              </p>
              <Link to="/daftar" data-testid="cta-daftar-bukti" className="tombol-utama inline-block mt-6 px-6 py-3 rounded">
                Buat ruang kerja
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
